// src/components/TrendPanel.jsx
// 📊 拆分为两个 Card：事件分析 + 实拍图

import React, { useState } from 'react';
import {
  MapPin,
  BarChart3,
  Camera,
  Clock,
  Droplets,
  TrendingUp,
  Calendar,
  ExternalLink
} from 'lucide-react';
import Card from './ui/Card/Card';
import Badge from './ui/Badge/Badge';
import Button from './ui/Button/Button';
import SingleEventTimeSeries from './SingleEventTimeSeries';
import FloodFrequencyHeatmap from './FloodFrequencyHeatmap';
import WaterDepthVisual from './dashboard/EventDetails/WaterDepthVisual';
import MyCoastImage from './MyCoastImage';
import { useNearbyImages } from '../hooks/useMyCoast';
import { formatDateTime, getSeverityVariant, getSeverityIcon } from '../utils/dateFormatter';
import { formatMyCoastTime } from '../utils/myCoastAPI';

const TrendPanel = ({ selectedSensor }) => {
  const [viewMode, setViewMode] = useState('timeline');
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const [hoveredPointData, setHoveredPointData] = useState(null); // 新增

  const {
    nearbyImages,
    isLoading: imagesLoading,
    hasImages,
    imageCount
  } = useNearbyImages(selectedSensor, 3, 8);

  // 🔧 处理时间轴悬停
const handleTimelineHover = (pointIndex, pointData) => {
  setHighlightedIndex(pointIndex);
  setHoveredPointData(pointData);
};

const handleTimelineLeave = () => {
  setHighlightedIndex(null);
  setHoveredPointData(null);
};

  if (!selectedSensor) {
    return (
      <Card className="h-full">
        <Card.Header className="border-b bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            📊 History Event
          </h2>
        </Card.Header>
        <Card.Body className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MapPin className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">No sensor selected</p>
            <p className="text-xs text-gray-500">Click on a sensor to view historical data</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const latestEvent = selectedSensor.events?.[0];
  const selectedEvent = selectedSensor.events?.[selectedEventIndex] || latestEvent;

  if (!latestEvent) {
    return (
      <Card className="h-full">
        <Card.Header className="border-b bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            📊 History Event
          </h2>
        </Card.Header>
        <Card.Body className="flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900 mb-1">
              📍 {selectedSensor.location.slice(0, 30)}...
            </p>
            <p className="text-xs text-gray-500">No historical event data available</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const handleImageClick = (image, index) => {
    setSelectedImageIndex(selectedImageIndex === index ? null : index);
  };

  const openMyCoastPage = (image) => {
    if (!image || !image.lat || !image.lon) return;
    const url = `https://mycoast.org/?lat=${image.lat}&lng=${image.lon}&zoom=15`;
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* 上半卡片：事件分析 */}
      <Card className="flex flex-col max-h-[60%] min-h-0 overflow-hidden">
        <Card.Header className="flex-shrink-0 border-b bg-gray-50/50">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                📊 History Event
              </h2>
              <Badge
                variant={getSeverityVariant(latestEvent.severity)}
                size="sm"
                icon={getSeverityIcon(latestEvent.severity)}
                pulse={latestEvent.severity === 'severe'}
              >
                {latestEvent.water_depth_inches}"
              </Badge>
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-gray-900 leading-tight">
                📍 {selectedSensor.location}
              </h3>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {selectedSensor.borough}
              </p>
            </div>
            {selectedSensor.events?.length > 1 && (
              <div className="pt-2 border-t border-gray-200">
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  Select Event ({selectedSensor.events.length} total):
                </label>
                <select
                  value={selectedEventIndex}
                  onChange={(e) => setSelectedEventIndex(parseInt(e.target.value))}
                  className="w-full text-xs border border-gray-300 rounded-md px-2 py-1"
                >
                  {selectedSensor.events.map((event, index) => (
                    <option key={index} value={index}>
                      {new Date(event.timestamp).toLocaleDateString()} - {event.water_depth_inches}" ({event.severity})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="flex-1 overflow-y-auto space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-900 mb-2">
              {selectedEvent.description}
            </p>
            <div className="flex items-center text-xs text-gray-600">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
              <span>{formatDateTime(selectedEvent.timestamp)}</span>
            </div>
          </div>

<WaterDepthVisual
  waterDepthInches={selectedEvent.water_depth_inches}
  severity={selectedEvent.severity}
  showTireChart={true}
  showDetailedStats={true}
  isInteractive={true}
  selectedPoint={highlightedIndex}
  hoveredPointData={hoveredPointData}
  isHovering={hoveredPointData !== null}
/>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <TrendingUp className="h-4 w-4 text-green-500" />
                Temporal Analysis
              </h4>
              <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-1.5 text-xs rounded-md ${viewMode === 'timeline' ? 'bg-white text-blue-600 border' : 'text-gray-600'}`}
                >
                  <Clock className="h-3 w-3 inline-block mr-1" /> Timeline
                </button>
                <button
                  onClick={() => setViewMode('pattern')}
                  className={`px-3 py-1.5 text-xs rounded-md ${viewMode === 'pattern' ? 'bg-white text-blue-600 border' : 'text-gray-600'}`}
                >
                  <Calendar className="h-3 w-3 inline-block mr-1" /> Pattern
                </button>
              </div>
            </div>
            {viewMode === 'timeline' ? (
<SingleEventTimeSeries
  selectedEvent={selectedEvent}
  onPointHover={handleTimelineHover}
  onPointLeave={handleTimelineLeave}
/>
            ) : (
              <FloodFrequencyHeatmap sensorEvents={selectedSensor.events} />
            )}
          </div>
        </Card.Body>
      </Card>

      {/* 下半卡片：MyCoast 图像 */}
       <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <Card.Header className="flex-shrink-0 border-b bg-gray-50/50">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
              <Camera className="h-4 w-4 text-purple-500" />
              Real MyCoast Images
            </h4>
            {hasImages && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                {imageCount} found
              </span>
            )}
          </div>
        </Card.Header>

     <Card.Body className="flex-1 overflow-y-auto space-y-3">
          {imagesLoading && <div>Loading...</div>}
          {!imagesLoading && !hasImages && <div className="text-sm text-gray-500">No images found</div>}
          {hasImages && (
            <div className="grid grid-cols-2 gap-3">
              {nearbyImages.slice(0, 4).map((image, i) => (
                <div
                  key={image.id || i}
                  className={`rounded-lg overflow-hidden border transition-all duration-200 cursor-pointer ${selectedImageIndex === i ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:ring-1 hover:ring-gray-400 hover:shadow-md'}`}
                  onClick={() => handleImageClick(image, i)}
                >
                  <MyCoastImage
                    src={image.picUrl}
                    alt={`MyCoast ${i + 1}`}
                    className="w-full h-40 object-cover"
                    showPlaceholder={true}
                  />
                </div>
              ))}
            </div>
          )}

          {selectedImageIndex !== null && nearbyImages[selectedImageIndex] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-blue-900">📸 Real MyCoast Photo</span>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<ExternalLink className="h-3 w-3" />}
                  onClick={() => openMyCoastPage(nearbyImages[selectedImageIndex])}
                >
                  View Original
                </Button>
              </div>
              <div className="text-xs text-blue-700 space-y-1">
                <div>📍 {nearbyImages[selectedImageIndex].address}</div>
                <div>🕒 {formatMyCoastTime(nearbyImages[selectedImageIndex].datetime)}</div>
                <div>📏 {nearbyImages[selectedImageIndex].distance?.toFixed(2)}km away</div>
              </div>
              {nearbyImages[selectedImageIndex].comment && (
                <div className="text-xs italic text-blue-600 mt-1 bg-blue-100 p-2 rounded">
                  "{nearbyImages[selectedImageIndex].comment.slice(0, 100)}..."
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TrendPanel;
